import { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { ChapterTag, MaskedLine, Reveal } from "@/components/bits";
import Lightbox from "@/components/Lightbox";

const NEARBY = [
  { place: "Kempegowda International Airport", via: "Via NH 44" },
  { place: "Stonehill International School", via: "Minutes away" },
  { place: "Delhi Public School", via: "Yelahanka" },
  { place: "Manipal & Sparsh Hospitals", via: "Bellary Road" },
  { place: "Phoenix Mall of Asia", via: "Yelahanka Junction" },
  { place: "Angsana Oasis Spa & Resort", via: "Doddaballapura Road" },
];

const MAPS_URL =
  "https://www.google.com/maps/search/?api=1&query=Embassy+Origins+Rajanukunte+Bengaluru";

export default function Location() {
  const [mapOpen, setMapOpen] = useState(false);
  return (
    <section id="location" className="bg-[#F5F4EF] py-24 md:py-36" data-testid="location-section">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <ChapterTag num="06" label="Rooted in Convenience" />
            <h2 className="font-display mt-12 text-4xl font-light leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              <MaskedLine inView delay={0.05}>Away from the noise.</MaskedLine>
              <MaskedLine inView delay={0.15}>
                <span className="italic text-[#064E3B]">Close to everything.</span>
              </MaskedLine>
            </h2>
            <Reveal delay={0.3} className="mt-10 max-w-md text-base leading-relaxed text-[#57534E]">
              <p>
                Set near Yelahanka — a nature-friendly part of the city that sits away from the
                noise, yet stays well connected to its key destinations via NH 44 and Bellary Road.
              </p>
            </Reveal>
            <Reveal delay={0.4} className="mt-12 flex flex-col items-start gap-5">
              <button
                onClick={() => setMapOpen(true)}
                data-testid="view-location-map-btn"
                className="group text-[11px] font-semibold uppercase tracking-[0.28em] text-[#064E3B]"
              >
                <span className="border-b border-[#064E3B]/40 pb-1 transition-colors duration-300 group-hover:border-[#A98B5F] group-hover:text-[#A98B5F]">
                  View location map
                </span>
              </button>
              <a
                href={MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="google-maps-link"
                className="group flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.28em] text-[#57534E] transition-colors duration-300 hover:text-[#A98B5F]"
              >
                View location on Google Maps
                <ArrowUpRight size={13} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </a>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <div className="border-t border-[#1C1917]/15 lg:mt-24" data-testid="location-list">
              {NEARBY.map((n, i) => (
                <Reveal key={n.place} delay={0.1 + i * 0.06}>
                  <div className="group flex items-baseline justify-between gap-6 border-b border-[#1C1917]/15 py-6">
                    <span className="font-display text-xl font-light tracking-tight transition-colors duration-300 group-hover:text-[#064E3B] md:text-2xl">
                      {n.place}
                    </span>
                    <span className="whitespace-nowrap text-xs uppercase tracking-[0.2em] text-[#57534E]">
                      {n.via}
                    </span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
      <Lightbox
        open={mapOpen}
        onClose={() => setMapOpen(false)}
        src="/images/map.jpg"
        alt="Location map of Embassy Origins near Yelahanka with schools, hospitals, lakes and KIAL Airport"
        caption="Embassy Origins · Rajanukunte, North Bengaluru"
      />
    </section>
  );
}
